/**
 * Capability registry — the vocabulary of things an embedding host may lock.
 *
 * Tile owns these keys; the host (Apptile) owns which of them a given
 * merchant has. That split is deliberate: the host knows about plans and
 * pricing, Tile knows about features, and neither has to learn the other's
 * model. What crosses the wire is a `locked` array of keys from this file —
 * no plan names, no tiers, no ordering.
 *
 * DENY-LIST, not allow-list: anything absent from `locked` is available.
 * A new Tile feature therefore ships unlocked and needs no coordinated
 * change on the host. The tradeoff is that a *typo'd or renamed* key silently
 * unlocks a feature rather than locking it, which is why the host's list is
 * validated against this registry at session start (see embed-session.ts) and
 * unknown keys are logged loudly.
 *
 * Adding one:
 *   1. add the key here,
 *   2. call `useCapability(CAPABILITIES.X)` at the entry point(s),
 *   3. tell the host the key exists so it can include it for the right plans.
 * Nothing else in the transport changes.
 *
 * Keep keys COARSE (`screen.create`, not one per screen kind) — they ride in
 * the embed token, which travels in a URL fragment.
 *
 * NOTE: this is presentation gating. It decides what the editor offers, not
 * what the server permits. Anything that must actually hold has to be
 * enforced by tile-backend on the write path as well.
 */

export const CAPABILITIES = {
  /** Creating a new screen — every entry point (panel, all-screens canvas). */
  SCREEN_CREATE: 'screen.create',
  /**
   * Publishing — the top-bar button that ships Live Layer config to
   * production. Coarse on purpose: one key for "can this merchant make
   * changes go live", not one per publish mechanism.
   */
  PUBLISH: 'publish',
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

export const ALL_CAPABILITIES: readonly Capability[] = Object.values(CAPABILITIES);

/** True when `key` is a capability this build knows how to enforce. */
export function isKnownCapability(key: string): key is Capability {
  return (ALL_CAPABILITIES as readonly string[]).includes(key);
}
